import type { FindingCategory, Hydrant, InspectionResult } from "./types.ts";
import { displayTypeFor } from "./hydrants.ts";
import { currentUser } from "./auth.ts";
import { createInspection, listFindingTypes, listInspectionsFor } from "./inspections.ts";

const RESULT_LABELS: Record<InspectionResult, string> = {
  ok: "In Ordnung",
  maengel: "Mängel festgestellt",
  defect: "Defekt",
  blocked: "Zugestellt / keine Prüfung möglich",
  missing: "Nicht auffindbar",
};

const CATEGORY_LABELS: Record<FindingCategory, string> = {
  beschilderung_zubehoer: "Beschilderung / Zubehör",
  hydrant: "Hydrant",
  ventil_schieber: "Ventil / Schieber",
  zugaenglichkeit: "Zugänglichkeit / Mähstatus",
};

const TYPE_LABELS: Record<string, string> = {
  pillar: "Überflurhydrant",
  underground: "Unterflurhydrant",
  wall: "Wandhydrant",
  pipe: "Rohrhydrant",
  pond: "Löschteich",
  suction: "Sauganschluss",
  other: "Sonstiges",
};

// Deutsche Labels für die OSM-Tag-Werte (siehe OSM-Wiki emergency=fire_hydrant).
const POSITION_LABELS: Record<string, string> = {
  lane: "Fahrbahn",
  sidewalk: "Gehweg",
  parking_lot: "Parkplatz",
  green: "Grünfläche",
};

const WATER_SOURCE_LABELS: Record<string, string> = {
  main: "Leitungsnetz",
  water_tank: "Löschwasserbehälter",
  pond: "Teich",
  stream: "Bach",
  river: "Fluss",
  lake: "See",
  canal: "Kanal",
  groundwater: "Grundwasser",
};

export function mountSidebar(
  sidebar: HTMLElement,
  content: HTMLElement,
  closeBtn: HTMLButtonElement,
  getHydrant: (id: string) => Hydrant | undefined,
): (h: Hydrant) => void {
  closeBtn.addEventListener("click", () => {
    sidebar.hidden = true;
    content.innerHTML = "";
  });

  return function show(h: Hydrant) {
    sidebar.hidden = false;
    void renderSidebar(content, h, getHydrant, show);
  };
}

function fmt(value: number | null, suffix: string) {
  return value == null ? "—" : `${value} ${suffix}`;
}

async function renderSidebar(
  root: HTMLElement,
  h: Hydrant,
  getHydrant: (id: string) => Hydrant | undefined,
  show: (h: Hydrant) => void,
): Promise<void> {
  root.innerHTML = "";

  const title = document.createElement("h2");
  title.className = "hydrant-title";
  title.textContent = TYPE_LABELS[displayTypeFor(h.hydrant_type, h.tags)] ?? "Hydrant";
  if (h.ref) {
    const badge = document.createElement("span");
    badge.className = "badge";
    badge.textContent = `Nr. ${h.ref}`;
    title.append(badge);
  }
  root.append(title);

  if (h.deleted_at) {
    const banner = document.createElement("div");
    banner.className = "deleted-banner";
    const text = document.createElement("div");
    text.textContent =
      `In OSM gelöscht am ${new Date(h.deleted_at).toLocaleDateString("de-DE")}. ` +
      "Der Eintrag bleibt samt Prüfhistorie erhalten.";
    banner.append(text);
    const successor = h.replaced_by ? getHydrant(h.replaced_by) : undefined;
    if (successor) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "linkish";
      btn.textContent = "Zum Nachfolger-Hydranten →";
      btn.addEventListener("click", () => show(successor));
      banner.append(btn);
    }
    root.append(banner);
  }

  const osmLink = document.createElement("a");
  osmLink.href = `https://www.openstreetmap.org/${h.osm_type}/${h.osm_id}`;
  osmLink.target = "_blank";
  osmLink.rel = "noopener";
  osmLink.textContent = "In OSM öffnen";
  osmLink.style.fontSize = "0.85rem";
  root.append(osmLink);

  const t = h.tags;
  // fire_hydrant:pressure kennt laut Wiki auch Sonderwerte jenseits von Zahlen.
  const pressureTag = t["fire_hydrant:pressure"];
  const pressure =
    pressureTag === "suction"
      ? "Saugbetrieb (Pumpe erforderlich)"
      : pressureTag === "yes"
        ? "vorhanden"
        : fmt(h.pressure_bar, "bar");
  const couplings = t["couplings:type"]
    ? t["couplings:type"] +
      (t["couplings:diameters"] ? ` (${t["couplings:diameters"].split(";").join("; ")})` : "")
    : "—";

  const dl = document.createElement("dl");
  dl.className = "tags";
  const rows: [string, string][] = [
    // fire_hydrant:diameter ist die Nennweite (DN nach ISO 6708), keine mm-Angabe.
    ["Nennweite", h.diameter_mm != null ? `DN ${h.diameter_mm}` : "—"],
    ["Druck", pressure],
    ["Wasserquelle", t.water_source ? (WATER_SOURCE_LABELS[t.water_source] ?? t.water_source) : "—"],
    ["Kupplungen", couplings],
    ["Förderleistung", fmt(h.flow_capacity_lpm, "l/min")],
    ["Lage", h.position ? (POSITION_LABELS[h.position] ?? h.position) : "—"],
    ["Nummer", h.ref ?? (t.noref === "yes" ? "ohne Nummer" : "—")],
    ["OSM-Sichtprüfung", t["survey:date"] ?? "—"],
    ["Farbe", h.colour ?? "—"],
    ["Position", `${h.lat.toFixed(5)}, ${h.lon.toFixed(5)}`],
  ];
  for (const [k, v] of rows) {
    const dt = document.createElement("dt");
    dt.textContent = k;
    const dd = document.createElement("dd");
    dd.textContent = v;
    dl.append(dt, dd);
  }
  root.append(dl);

  const section = document.createElement("section");
  section.className = "inspections";
  const h3 = document.createElement("h3");
  h3.textContent = "Prüfungen";
  section.append(h3);

  const list = document.createElement("div");
  section.append(list);
  root.append(section);

  const user = await currentUser();
  if (user) {
    section.append(renderInspectionForm(h.id, () => refreshList(list, h.id)));
  } else {
    const hint = document.createElement("p");
    hint.className = "empty";
    hint.textContent = "Zum Erfassen einer Prüfung oben einloggen.";
    section.append(hint);
  }

  await refreshList(list, h.id);
}

async function refreshList(list: HTMLElement, hydrantId: string): Promise<void> {
  list.innerHTML = "<p class=\"empty\">Lade Prüfungen …</p>";
  try {
    const inspections = await listInspectionsFor(hydrantId);
    list.innerHTML = "";
    if (inspections.length === 0) {
      const p = document.createElement("p");
      p.className = "empty";
      p.textContent = "Noch keine Prüfung erfasst.";
      list.append(p);
      return;
    }
    for (const i of inspections) {
      const item = document.createElement("div");
      item.className = "inspection-item";
      const head = document.createElement("strong");
      head.textContent = `${new Date(i.inspected_at).toLocaleDateString("de-DE")} – ${RESULT_LABELS[i.result]}`;
      const meta = document.createElement("div");
      meta.className = "meta";
      const parts = [i.inspector];
      if (i.flow_lpm != null) parts.push(`${i.flow_lpm} l/min`);
      if (i.pressure_bar != null) parts.push(`${i.pressure_bar} bar`);
      meta.textContent = parts.join(" · ");
      item.append(head, meta);
      const findings = (i.inspection_findings ?? [])
        .slice()
        .sort((a, b) => (a.finding_types?.sort ?? 99) - (b.finding_types?.sort ?? 99));
      if (findings.length > 0) {
        const fl = document.createElement("div");
        fl.className = "findings";
        for (const f of findings) {
          const chip = document.createElement("span");
          chip.className = "finding-chip";
          chip.textContent = f.finding_types?.label ?? f.code;
          fl.append(chip);
        }
        item.append(fl);
      }
      if (i.notes) {
        const notes = document.createElement("div");
        notes.textContent = i.notes;
        item.append(notes);
      }
      list.append(item);
    }
  } catch (err) {
    list.innerHTML = "";
    const p = document.createElement("p");
    p.className = "empty";
    p.textContent = "Fehler beim Laden der Prüfungen.";
    list.append(p);
    console.error(err);
  }
}

function renderInspectionForm(hydrantId: string, onSaved: () => void): HTMLElement {
  const form = document.createElement("form");
  form.className = "form-grid";
  form.innerHTML = `
    <label for="ins-date">Datum</label>
    <input id="ins-date" name="inspected_at" type="date" required />
    <label for="ins-who">Prüfer</label>
    <input id="ins-who" name="inspector" type="text" required />
    <label for="ins-result">Ergebnis</label>
    <select id="ins-result" name="result" required>
      <option value="ok">In Ordnung</option>
      <option value="maengel">Mängel festgestellt</option>
      <option value="defect">Defekt</option>
      <option value="blocked">Zugestellt / keine Prüfung möglich</option>
      <option value="missing">Nicht auffindbar</option>
    </select>
    <fieldset class="findings-select" data-findings>
      <legend>Befunde</legend>
    </fieldset>
    <label for="ins-flow">Förderl. l/min</label>
    <input id="ins-flow" name="flow_lpm" type="number" min="0" step="1" />
    <label for="ins-pressure">Druck (bar)</label>
    <input id="ins-pressure" name="pressure_bar" type="number" min="0" step="0.1" />
    <label for="ins-notes">Notizen</label>
    <textarea id="ins-notes" name="notes"></textarea>
  `;
  const dateInput = form.querySelector<HTMLInputElement>('input[name="inspected_at"]')!;
  dateInput.valueAsDate = new Date();

  // Befund-Checkboxen aus dem Katalog, gruppiert nach Kategorie.
  // Layout-Container innerhalb des Fieldsets: fieldset selbst ignoriert
  // display:flex in etlichen (v. a. mobilen) Browsern.
  const findingsBox = form.querySelector<HTMLFieldSetElement>("[data-findings]")!;
  const list = document.createElement("div");
  list.className = "findings-list";
  findingsBox.append(list);
  void listFindingTypes().then((types) => {
    let lastCategory: string | null = null;
    for (const t of types) {
      if (t.category !== lastCategory) {
        lastCategory = t.category;
        const heading = document.createElement("strong");
        heading.textContent = CATEGORY_LABELS[t.category] ?? t.category;
        list.append(heading);
      }
      const label = document.createElement("label");
      const cb = document.createElement("input");
      cb.type = "checkbox";
      cb.name = "finding";
      cb.value = t.code;
      const text = document.createElement("span");
      text.textContent = t.label;
      label.append(cb, text);
      list.append(label);
    }
  });

  const actions = document.createElement("div");
  actions.className = "form-actions";
  const save = document.createElement("button");
  save.type = "submit";
  save.textContent = "Prüfung speichern";
  actions.append(save);
  form.append(actions);

  form.addEventListener("submit", async (ev) => {
    ev.preventDefault();
    save.disabled = true;
    const fd = new FormData(form);
    const flow = fd.get("flow_lpm") as string;
    const pressure = fd.get("pressure_bar") as string;
    const findingCodes = fd.getAll("finding").map(String);
    let result = fd.get("result") as InspectionResult;
    // Befunde angekreuzt, aber "In Ordnung" gewählt → automatisch auf Mängel heben.
    if (result === "ok" && findingCodes.length > 0) result = "maengel";
    try {
      await createInspection({
        hydrant_id: hydrantId,
        inspected_at: fd.get("inspected_at") as string,
        inspector: (fd.get("inspector") as string).trim(),
        result,
        flow_lpm: flow ? Number(flow) : null,
        pressure_bar: pressure ? Number(pressure) : null,
        notes: ((fd.get("notes") as string) || "").trim() || null,
      }, findingCodes);
      form.reset();
      dateInput.valueAsDate = new Date();
      onSaved();
    } catch (err) {
      alert("Speichern fehlgeschlagen: " + (err as Error).message);
    } finally {
      save.disabled = false;
    }
  });

  return form;
}
